/**
 * Loyalty page hero + tier ladder.
 */
import { ArrowRight, Sparkles } from "lucide-react";

const TIERS = [
    {
        tier: "Seedling",
        mult: "1×",
        band: "Under $500 / 180d",
        perks: ["1 pt per $1 spent", "Birthday reward", "Early menu previews"],
    },
    {
        tier: "Cultivator",
        mult: "1.25×",
        band: "$500 – $1,499 / 180d",
        perks: ["1.25 pts per $1", "Free pre-roll each quarter", "Member-only drops"],
    },
    {
        tier: "Connoisseur",
        mult: "1.5×",
        band: "$1,500 – $2,999 / 180d",
        perks: ["1.5 pts per $1", "Monthly gift on us", "Priority on new arrivals"],
        highlight: true,
    },
    {
        tier: "Curator",
        mult: "1.75×",
        band: "$3,000 – $4,999 / 180d",
        perks: ["1.75 pts per $1", "VIP tastings & events", "Concierge text line"],
    },
    {
        tier: "Apex",
        mult: "2×",
        band: "$5,000+ / 180d",
        perks: ["2 pts per $1", "Private shopping by appt", "Annual collector's gift"],
    },
];

export default function LoyaltyHero({ onOpenModal }) {
    return (
        <>
            <section className="bg-zen-bg">
                <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-10 sm:py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-5 inline-flex items-center gap-1.5">
                            <Sparkles size={11} /> ZenCircle Rewards
                        </p>
                        <h1
                            data-testid="loyalty-hero-h1"
                            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-zen-ink leading-[1.05] tracking-tight mb-7"
                        >
                            Stay close.{" "}
                            <span className="italic text-zen-olive">Get rewarded.</span>
                        </h1>
                        <p className="text-base sm:text-lg text-zen-ink-soft max-w-xl mb-9 leading-relaxed">
                            Our free loyalty program turns every visit into points,
                            every point into dollars, and every regular into something
                            closer to a neighbor. Five tiers, a 180-day rolling window,
                            and perks that quietly compound the longer you shop with us.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <button
                                onClick={onOpenModal}
                                data-testid="loyalty-open-modal"
                                className="bg-zen-ink text-zen-cream px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-olive-dark transition-colors inline-flex items-center gap-2"
                            >
                                <Sparkles size={13} /> Check my points
                            </button>
                            <a
                                href="https://3609.aiqapps.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="loyalty-join-mobile"
                                className="border border-zen-ink text-zen-ink px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-ink hover:text-zen-cream transition-colors inline-flex items-center gap-2"
                            >
                                Join on mobile <ArrowRight size={13} />
                            </a>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="aspect-[4/5] overflow-hidden bg-zen-elevated">
                            <img
                                src="https://customer-assets.emergentagent.com/job_zenzest-near-me/artifacts/xkyk453i_ZenZest%20Pic.jpeg"
                                alt="ZenCircle Rewards"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section
                data-testid="loyalty-tiers"
                className="bg-zen-surface border-y border-zen-line"
            >
                <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-12">
                    <div className="mb-8">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                            Five tiers · 180-day rolling spend
                        </p>
                        <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-zen-ink leading-tight tracking-tight max-w-2xl">
                            The more you shop, the more it gives back.
                        </h2>
                        <p className="text-sm sm:text-base text-zen-ink-soft mt-5 max-w-2xl leading-relaxed">
                            Your spend over any rolling 180-day window unlocks your
                            multiplier. We apply it automatically — in-store, online,
                            on delivery — so the rate just shows up.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                        {TIERS.map((t) => (
                            <div
                                key={t.tier}
                                data-testid={`loyalty-tier-${t.tier.toLowerCase()}`}
                                className={`relative p-6 border bg-zen-bg ${
                                    t.highlight
                                        ? "border-zen-olive shadow-[0_8px_32px_rgba(107,124,79,0.18)]"
                                        : "border-zen-line"
                                }`}
                            >
                                {t.highlight && (
                                    <p className="absolute -top-2.5 left-6 bg-zen-olive text-zen-bg text-[9px] uppercase tracking-[0.22em] px-2 py-0.5">
                                        Sweet spot
                                    </p>
                                )}
                                <p className="font-serif font-light text-4xl text-zen-olive leading-none mb-1">
                                    {t.mult}
                                    <span className="text-sm text-zen-ink-soft ml-1">
                                        pts
                                    </span>
                                </p>
                                <h3 className="font-serif text-xl text-zen-ink font-light mt-4 mb-1">
                                    {t.tier}
                                </h3>
                                <p className="text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft mb-4">
                                    {t.band}
                                </p>
                                <ul className="space-y-2">
                                    {t.perks.map((p) => (
                                        <li
                                            key={p}
                                            className="text-xs text-zen-ink-soft leading-relaxed flex gap-2"
                                        >
                                            <span className="text-zen-olive">·</span>
                                            <span>{p}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
